"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import {
  Check,
  ChevronRight,
  Disc3,
  Loader2,
  Pause,
  Play,
  Plus,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getMusicCollectionSongs,
  getMusicTopSongs,
  searchMusic,
} from "@/actions/music";
import {
  ChatDrawerHeader,
  ChatSearchInput,
} from "@/components/chat/chat-ui-primitives";
import { Button } from "@/components/ui/button";
import { musicArtworkSrc } from "@/lib/music/links";
import type { MusicAlbum, MusicArtist, MusicSong } from "@/lib/music/types";
import { cn } from "@/lib/utils";
import {
  connectAppleMusic,
  disconnectAppleMusic,
  getAppleMusicRecentlyPlayed,
  isAppleMusicConnectSupported,
  wasAppleMusicConnected,
} from "./apple-music-connect";
import { PlayingBars } from "./playing-bars";
import { type PreviewTarget, useMusicPreview } from "./use-music-preview";

/**
 * The catalog browser every music picker shares: search (songs, artists,
 * albums), the storefront's top songs, artist/album drill-ins with a back step
 * inside the sheet, optional Apple Music "recently played", and a preview on
 * every row. Pickers wrap it in their own drawer and decide what "select"
 * means — up to five profile picks, or one song for a post / banner / chat.
 *
 * Renders as flex children of a `DrawerContent`: header, `topSlot`, search,
 * scrolling results.
 */

export const musicKeys = {
  mine: () => ["music", "mine"] as const,
  top: () => ["music", "top"] as const,
  search: (query: string) => ["music", "search", query] as const,
  collection: (kind: string, id: string) =>
    ["music", "collection", kind, id] as const,
  recent: () => ["music", "apple-recent"] as const,
};

type View =
  | { kind: "browse" }
  | { kind: "artist" | "album"; id: string; title: string };

function unwrap<T>(
  result: { ok: true; data: T } | { ok: false; message: string },
) {
  if (!result.ok) throw new Error(result.message);
  return result.data;
}

export function MusicBrowser({
  open,
  title,
  subtitle,
  topSlot,
  isSelected,
  selectionFull = false,
  onSelect,
  previewSurface,
}: {
  /** Queries run and state resets per opening. */
  open: boolean;
  title: string;
  subtitle?: string;
  /** Between the header and the search field (e.g. the picked strip). */
  topSlot?: ReactNode;
  isSelected: (songId: string) => boolean;
  /** No more room: unselected rows can't be added. */
  selectionFull?: boolean;
  onSelect: (song: MusicSong) => void;
  previewSurface: PreviewTarget["surface"];
}) {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<View>({ kind: "browse" });
  const debouncedQuery = useDebounce(query.trim(), 300);
  const preview = useMusicPreview();

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setView({ kind: "browse" });
  }, [open]);

  useEffect(() => {
    if (!open) preview.pause();
  }, [open, preview.pause]);

  const rowProps: RowProps = {
    isSelected,
    full: selectionFull,
    previewKey: preview.key,
    previewStatus: preview.status,
    onToggle: onSelect,
    onPreview: (song) =>
      preview.toggle({
        key: song.id,
        previewUrl: song.previewUrl,
        surface: previewSurface,
      }),
  };

  return (
    <>
      <ChatDrawerHeader
        title={view.kind === "browse" ? title : view.title}
        subtitle={
          view.kind === "browse"
            ? subtitle
            : view.kind === "artist"
              ? "Top songs"
              : "Album"
        }
        onBack={
          view.kind === "browse" ? undefined : () => setView({ kind: "browse" })
        }
      />

      {topSlot}

      {view.kind === "browse" ? (
        <div className="px-4 pb-2">
          <ChatSearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search songs, artists, albums"
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-4">
        {view.kind !== "browse" ? (
          <CollectionSongs view={view} {...rowProps} />
        ) : debouncedQuery ? (
          <SearchResults
            query={debouncedQuery}
            onOpenArtist={(artist) =>
              setView({ kind: "artist", id: artist.id, title: artist.name })
            }
            onOpenAlbum={(album) =>
              setView({ kind: "album", id: album.id, title: album.title })
            }
            {...rowProps}
          />
        ) : (
          <BrowseHome open={open} {...rowProps} />
        )}
      </div>
    </>
  );
}

type RowProps = {
  isSelected: (songId: string) => boolean;
  full: boolean;
  previewKey: string | null;
  previewStatus: string;
  onToggle: (song: MusicSong) => void;
  onPreview: (song: Pick<MusicSong, "id" | "previewUrl">) => void;
};

/**
 * One result. Previewing has to be obvious: the artwork always carries a play
 * button, tapping anywhere on the row's text plays too, and the playing row
 * shows bouncing bars and a "Playing" tag. Only the + / ✓ adds.
 */
function SongRow({
  song,
  isSelected,
  full,
  previewKey,
  previewStatus,
  onToggle,
  onPreview,
}: RowProps & { song: MusicSong }) {
  const art = musicArtworkSrc(song.artworkUrl, 96);
  const selected = isSelected(song.id);
  const current = previewKey === song.id;
  const playing =
    current && (previewStatus === "playing" || previewStatus === "loading");
  const canPreview = Boolean(song.previewUrl);
  const preview = () => {
    if (canPreview) onPreview(song);
  };
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors duration-150",
        playing && "bg-surface-alt dark:bg-glass-bg-10",
      )}
    >
      <button
        type="button"
        disabled={!canPreview}
        onClick={preview}
        aria-label={
          !canPreview
            ? `No preview for ${song.title}`
            : playing
              ? `Pause ${song.title}`
              : `Play a preview of ${song.title}`
        }
        className="relative size-12 shrink-0 overflow-hidden rounded-lg bg-surface-alt active:scale-95 dark:bg-glass-bg-10"
      >
        {art ? (
          // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
          <img
            src={art}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <Disc3 className="m-auto size-5 text-text-tertiary" />
        )}
        {canPreview ? (
          <span className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="flex size-7 items-center justify-center rounded-full bg-white/95 text-neutral-900 shadow-md">
              {playing ? (
                <Pause
                  className="size-3.5"
                  fill="currentColor"
                  strokeWidth={0}
                />
              ) : (
                <Play
                  className="ml-px size-3.5"
                  fill="currentColor"
                  strokeWidth={0}
                />
              )}
            </span>
          </span>
        ) : null}
      </button>
      <button
        type="button"
        tabIndex={-1}
        onClick={preview}
        disabled={!canPreview}
        className="flex min-w-0 flex-1 flex-col text-left disabled:cursor-default"
      >
        <span className="flex w-full items-center gap-1.5 text-sm font-medium text-text-primary">
          <span className="truncate">{song.title}</span>
          {song.explicit ? (
            <span className="shrink-0 rounded-[3px] bg-text-tertiary/25 px-1 text-[9px] leading-[14px] font-bold text-text-secondary">
              E
            </span>
          ) : null}
        </span>
        <span className="flex w-full items-center gap-1.5 text-xs text-text-secondary">
          {playing ? (
            <span className="flex shrink-0 items-center gap-1 font-semibold text-brand">
              <PlayingBars className="h-2.5" barClassName="w-[2px]" />
              Playing
            </span>
          ) : null}
          <span className="truncate">
            {song.artistName}
            {song.albumName ? ` · ${song.albumName}` : ""}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={() => onToggle(song)}
        disabled={!selected && full}
        aria-label={selected ? `Remove ${song.title}` : `Add ${song.title}`}
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-full border transition-[transform,background-color] duration-150 ease-out-strong active:scale-90 disabled:opacity-30",
          selected
            ? "border-transparent bg-text-primary text-background"
            : "border-border text-text-primary dark:border-glass-border-10",
        )}
      >
        {selected ? (
          <Check className="size-4" strokeWidth={3} />
        ) : (
          <Plus className="size-4" />
        )}
      </button>
    </div>
  );
}

function SongList({ songs, ...rowProps }: RowProps & { songs: MusicSong[] }) {
  return (
    <div className="flex flex-col">
      {songs.map((song) => (
        <SongRow key={song.id} song={song} {...rowProps} />
      ))}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="px-2 pt-3 pb-1.5 text-[11px] font-bold tracking-wider text-text-tertiary uppercase">
      {children}
    </h4>
  );
}

function StatusLine({
  loading,
  error,
  empty,
}: {
  loading?: boolean;
  error?: Error | null;
  empty?: string;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-text-tertiary" />
      </div>
    );
  }
  const message = error?.message ?? empty;
  if (!message) return null;
  return (
    <p className="px-4 py-10 text-center text-sm text-text-tertiary">
      {message}
    </p>
  );
}

function BrowseHome({ open, ...rowProps }: RowProps & { open: boolean }) {
  const top = useQuery({
    queryKey: musicKeys.top(),
    queryFn: async () => unwrap(await getMusicTopSongs()),
    enabled: open,
    staleTime: 30 * 60_000,
  });

  return (
    <>
      <AppleMusicLibrary {...rowProps} />
      <SectionLabel>Top songs right now</SectionLabel>
      {top.data ? (
        <SongList songs={top.data} {...rowProps} />
      ) : (
        <StatusLine loading={top.isPending} error={top.error} />
      )}
    </>
  );
}

function AppleMusicLibrary(rowProps: RowProps) {
  const supported = isAppleMusicConnectSupported();
  const [connected, setConnected] = useState(
    () => supported && wasAppleMusicConnected(),
  );
  const [connecting, setConnecting] = useState(false);
  const recent = useQuery({
    queryKey: musicKeys.recent(),
    queryFn: getAppleMusicRecentlyPlayed,
    enabled: connected,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!supported) return null;

  if (!connected) {
    return (
      <button
        type="button"
        disabled={connecting}
        onClick={async () => {
          setConnecting(true);
          try {
            setConnected(await connectAppleMusic());
          } catch {
            toast.error("Couldn't connect to Apple Music");
          } finally {
            setConnecting(false);
          }
        }}
        className="mx-2 mt-1 flex w-[calc(100%-1rem)] items-center gap-3 rounded-2xl border border-border bg-surface-alt px-3 py-2.5 text-left transition-transform duration-150 ease-out-strong active:scale-[0.98] dark:border-glass-border-10 dark:bg-glass-bg-05"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-[#fa2d48] to-[#fc5c7d] text-white">
          {connecting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Disc3 className="size-4" />
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium text-text-primary">
            Connect Apple Music
          </span>
          <span className="text-xs text-text-secondary">
            Optional — pick from what you&rsquo;ve been playing.
          </span>
        </span>
        <ChevronRight className="size-4 text-text-tertiary" />
      </button>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between pr-2">
        <SectionLabel>Recently played on Apple Music</SectionLabel>
        <button
          type="button"
          onClick={async () => {
            await disconnectAppleMusic();
            setConnected(false);
          }}
          className="pt-3 pb-1.5 text-[11px] text-text-tertiary pointer-fine:hover:text-text-secondary"
        >
          Disconnect
        </button>
      </div>
      {recent.data && recent.data.length > 0 ? (
        <SongList songs={recent.data.slice(0, 10)} {...rowProps} />
      ) : (
        <StatusLine
          loading={recent.isPending}
          error={recent.error}
          empty="Nothing played recently."
        />
      )}
    </>
  );
}

function SearchResults({
  query,
  onOpenArtist,
  onOpenAlbum,
  ...rowProps
}: RowProps & {
  query: string;
  onOpenArtist: (artist: MusicArtist) => void;
  onOpenAlbum: (album: MusicAlbum) => void;
}) {
  const search = useInfiniteQuery({
    queryKey: musicKeys.search(query),
    queryFn: async ({ pageParam }) =>
      unwrap(await searchMusic({ query, songOffset: pageParam })),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextSongOffset ?? undefined,
    staleTime: 5 * 60_000,
  });

  if (!search.data) {
    return <StatusLine loading={search.isPending} error={search.error} />;
  }
  const first = search.data.pages[0];
  const songs = search.data.pages.flatMap((page) => page.songs);
  if (songs.length === 0 && first.artists.length === 0) {
    return <StatusLine empty={`No results for “${query}”`} />;
  }

  return (
    <>
      {first.artists.length > 0 ? (
        <>
          <SectionLabel>Artists</SectionLabel>
          <div className="scrollbar-hide flex gap-3 overflow-x-auto px-2">
            {first.artists.map((artist) => {
              const art = musicArtworkSrc(artist.artworkUrl, 144);
              return (
                <button
                  key={artist.id}
                  type="button"
                  onClick={() => onOpenArtist(artist)}
                  className="flex w-18 shrink-0 flex-col items-center gap-1.5 active:scale-95"
                >
                  <span className="size-18 overflow-hidden rounded-full bg-surface-alt dark:bg-glass-bg-10">
                    {art ? (
                      // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
                      <img
                        src={art}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </span>
                  <span className="line-clamp-2 text-center text-[11px] leading-tight text-text-secondary">
                    {artist.name}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {songs.length > 0 ? (
        <>
          <SectionLabel>Songs</SectionLabel>
          <SongList songs={songs} {...rowProps} />
          {search.hasNextPage ? (
            <div className="flex justify-center py-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={search.isFetchingNextPage}
                onClick={() => search.fetchNextPage()}
              >
                {search.isFetchingNextPage ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : null}
                More songs
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {first.albums.length > 0 ? (
        <>
          <SectionLabel>Albums</SectionLabel>
          <div className="flex flex-col">
            {first.albums.map((album) => {
              const art = musicArtworkSrc(album.artworkUrl, 96);
              return (
                <button
                  key={album.id}
                  type="button"
                  onClick={() => onOpenAlbum(album)}
                  className="pointer-fine:hover:bg-surface-hover flex items-center gap-3 rounded-xl px-2 py-1.5 text-left"
                >
                  <span className="size-12 shrink-0 overflow-hidden rounded-lg bg-surface-alt dark:bg-glass-bg-10">
                    {art ? (
                      // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
                      <img
                        src={art}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-text-primary">
                      {album.title}
                    </span>
                    <span className="truncate text-xs text-text-secondary">
                      {album.artistName}
                      {album.releaseYear ? ` · ${album.releaseYear}` : ""}
                    </span>
                  </span>
                  <ChevronRight className="size-4 text-text-tertiary" />
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </>
  );
}

function CollectionSongs({
  view,
  ...rowProps
}: RowProps & { view: Extract<View, { kind: "artist" | "album" }> }) {
  const collection = useQuery({
    queryKey: musicKeys.collection(view.kind, view.id),
    queryFn: async () =>
      unwrap(await getMusicCollectionSongs({ kind: view.kind, id: view.id })),
    staleTime: 30 * 60_000,
  });
  if (!collection.data) {
    return (
      <StatusLine loading={collection.isPending} error={collection.error} />
    );
  }
  if (collection.data.length === 0) {
    return <StatusLine empty="No songs here." />;
  }
  return <SongList songs={collection.data} {...rowProps} />;
}
